/**
 * utils/imageProcessing.ts
 * Image validation, decoding, resizing, and format conversion utilities
 *
 * Provides helper functions for image processing operations used by ImageStorageService.
 * All limit parameters (maxSize, maxDimension, allowedTypes) are passed as arguments
 * to keep utilities decoupled from application constants.
 *
 * BROWSER-ONLY: Requires createImageBitmap and HTMLCanvasElement APIs.
 * Tests using JSDOM/Node will need to mock this module.
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Image dimensions in pixels
 */
export interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Result of decoding an image blob
 */
export interface DecodedImage {
  width: number;
  height: number;
  bitmap: ImageBitmap;
}

/**
 * Result of checking image against size/dimension limits
 */
export interface ImageLimitsResult {
  /** True if image is within all limits */
  withinLimits: boolean;
  /** True if file size exceeds maxSize */
  exceedsSize: boolean;
  /** True if either dimension exceeds maxDimension */
  exceedsDimensions: boolean;
}

/**
 * Result of resizing an image
 */
export interface ResizedImage {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

// ============================================================================
// Errors
// ============================================================================

/**
 * Error thrown during image processing operations
 */
export class ImageProcessingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageProcessingError';
  }
}

// ============================================================================
// Functions
// ============================================================================

/**
 * Validate that a MIME type is in the allowed list
 *
 * @param mimeType - MIME type to validate (e.g., 'image/png')
 * @param allowedTypes - Array of allowed MIME types
 * @returns True if mimeType is in allowedTypes
 *
 * @example
 * const allowed = ['image/png', 'image/jpeg', 'image/webp'];
 * validateImageMimeType('image/png', allowed); // true
 * validateImageMimeType('image/gif', allowed); // false
 */
export function validateImageMimeType(
  mimeType: string,
  allowedTypes: readonly string[]
): boolean {
  return allowedTypes.includes(mimeType);
}

/**
 * Decode an image blob and retrieve its dimensions
 *
 * Uses createImageBitmap for efficient off-main-thread decoding.
 * The returned ImageBitmap should be closed after use to free memory.
 *
 * @param blob - Image blob to decode
 * @returns Promise resolving to decoded image with dimensions and bitmap
 * @throws {ImageProcessingError} If the image cannot be decoded
 *
 * @example
 * const decoded = await decodeImage(imageBlob);
 * console.log(`${decoded.width}x${decoded.height}`);
 * // Remember to call decoded.bitmap.close() when done
 */
export async function decodeImage(blob: Blob): Promise<DecodedImage> {
  try {
    const bitmap = await createImageBitmap(blob);
    return {
      width: bitmap.width,
      height: bitmap.height,
      bitmap,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new ImageProcessingError(
      `Failed to decode image: ${message}. The file may be corrupted or not a valid image.`
    );
  }
}

/**
 * Check if an image is within size and dimension limits
 *
 * @param size - File size in bytes
 * @param dimensions - Image dimensions in pixels
 * @param maxSize - Maximum allowed file size in bytes
 * @param maxDimension - Maximum allowed dimension (width or height) in pixels
 * @returns Object indicating whether image is within limits
 *
 * @example
 * const result = checkImageLimits(
 *   1024 * 1024,        // 1MB
 *   { width: 2000, height: 1500 },
 *   5 * 1024 * 1024,    // 5MB max
 *   4096                // 4096px max dimension
 * );
 * // { withinLimits: true, exceedsSize: false, exceedsDimensions: false }
 */
export function checkImageLimits(
  size: number,
  dimensions: ImageDimensions,
  maxSize: number,
  maxDimension: number
): ImageLimitsResult {
  const exceedsSize = size > maxSize;
  const exceedsDimensions =
    dimensions.width > maxDimension || dimensions.height > maxDimension;

  return {
    withinLimits: !exceedsSize && !exceedsDimensions,
    exceedsSize,
    exceedsDimensions,
  };
}

/**
 * Resize an image to fit within a maximum dimension while maintaining aspect ratio
 *
 * Creates a canvas with the resized image. The input bitmap is closed after
 * drawing to prevent memory leaks.
 *
 * @param bitmap - ImageBitmap to resize
 * @param maxDimension - Maximum dimension (width or height) in pixels
 * @returns Resized image with canvas and new dimensions
 *
 * @example
 * const decoded = await decodeImage(blob);
 * const resized = resizeImage(decoded.bitmap, 4096);
 * console.log(`Resized to ${resized.width}x${resized.height}`);
 * // Note: decoded.bitmap is closed by this function
 */
export function resizeImage(
  bitmap: ImageBitmap,
  maxDimension: number
): ResizedImage {
  const { width: originalWidth, height: originalHeight } = bitmap;

  // Calculate scale factor to fit within maxDimension
  const scale = Math.min(
    maxDimension / originalWidth,
    maxDimension / originalHeight,
    1 // Don't upscale
  );

  const newWidth = Math.round(originalWidth * scale);
  const newHeight = Math.round(originalHeight * scale);

  // Create canvas and draw resized image
  const canvas = document.createElement('canvas');
  canvas.width = newWidth;
  canvas.height = newHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new ImageProcessingError('Failed to get canvas 2D context');
  }

  ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);

  // Close bitmap to free memory
  bitmap.close();

  return {
    canvas,
    width: newWidth,
    height: newHeight,
  };
}

/**
 * Convert a canvas to a WebP blob
 *
 * @param canvas - Canvas element containing the image
 * @param quality - WebP quality (0-1), defaults to 0.8
 * @returns Promise resolving to WebP blob
 * @throws {ImageProcessingError} If conversion fails
 *
 * @example
 * const resized = resizeImage(bitmap, 4096);
 * const webpBlob = await convertToWebP(resized.canvas, 0.85);
 */
export async function convertToWebP(
  canvas: HTMLCanvasElement,
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(
            new ImageProcessingError(
              'Failed to convert image to WebP. The browser may not support WebP encoding.'
            )
          );
        }
      },
      'image/webp',
      quality
    );
  });
}
